import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Payment,
  LocalShipping,
  Receipt,
  AccountBalance,
  Factory,
  TrendingUp,
  Warning,
  AttachMoney,
  Add,
  Event,
  Delete,
  PhoneInTalk,
  AddIcCall,
  CheckCircle,
  WhatsApp,
} from '@mui/icons-material';
import { MenuItem } from '@mui/material';
import TextField from '../components/common/TextField';
import api from '../services/api';
import type { CalendarEvent, LiabilitiesSummary, ReceivablesSummary } from '../types';
import ScheduleCallDialog from '../components/commercial/ScheduleCallDialog';
import CompleteCallDialog from '../components/commercial/CompleteCallDialog';
import { CALL_SHORTCUTS, telUrl, whatsappUrl } from '../components/commercial/contact';

const CATEGORY_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  purchases: { label: 'Compras', color: '#ef4444', icon: <Payment fontSize="small" /> },
  sales: { label: 'Ventas', color: '#22c55e', icon: <AttachMoney fontSize="small" /> },
  checks: { label: 'Cheques', color: '#f59e0b', icon: <AccountBalance fontSize="small" /> },
  'fixed-expenses': { label: 'Gastos Fijos', color: '#6366f1', icon: <Receipt fontSize="small" /> },
  production: { label: 'Producción', color: '#14b8a6', icon: <Factory fontSize="small" /> },
  opportunities: { label: 'Oportunidades', color: '#ec4899', icon: <TrendingUp fontSize="small" /> },
  inventory: { label: 'Vencimientos', color: '#dc2626', icon: <Warning fontSize="small" /> },
  custom: { label: 'Eventos', color: '#3b82f6', icon: <Event fontSize="small" /> },
  calls: { label: 'Llamados', color: '#0ea5e9', icon: <PhoneInTalk fontSize="small" /> },
};

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatCurrency = (amount: number, currency = 'ARS') => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(amount);
};

const CalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(Object.keys(CATEGORY_CONFIG));
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [liabilities, setLiabilities] = useState<LiabilitiesSummary | null>(null);
  const [receivables, setReceivables] = useState<ReceivablesSummary | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [scheduleCallOpen, setScheduleCallOpen] = useState(false);
  const [completeCall, setCompleteCall] = useState<CalendarEvent | null>(null);
  const [eventForm, setEventForm] = useState({ title: '', date: '', color: '#3b82f6', amount: '', description: '', flowType: '' as 'income' | 'expense' | '' });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const startOfMonth = useMemo(() => new Date(year, month, 1), [year, month]);
  const endOfMonth = useMemo(() => new Date(year, month + 1, 0), [year, month]);

  const calendarDays = useMemo(() => {
    const days: (Date | null)[] = [];
    const firstDayOfWeek = startOfMonth.getDay();

    // Días vacíos antes del primer día del mes
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    // Días del mes
    for (let day = 1; day <= endOfMonth.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [year, month, startOfMonth, endOfMonth]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const [eventsData, liabilitiesData, receivablesData] = await Promise.all([
        api.getCalendarEvents(start, end, selectedCategories.length === Object.keys(CATEGORY_CONFIG).length ? undefined : selectedCategories),
        api.getLiabilitiesSummary(),
        api.getReceivablesSummary(),
      ]);

      setEvents(eventsData);
      setLiabilities(liabilitiesData);
      setReceivables(receivablesData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos del calendario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [year, month, selectedCategories]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events.forEach(event => {
      const dateKey = new Date(event.date).toDateString();
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(event);
    });
    return map;
  }, [events]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleOpenCreate = () => {
    const today = new Date();
    setEventForm({
      title: '',
      date: today.toISOString().split('T')[0],
      color: '#3b82f6',
      amount: '',
      description: '',
      flowType: '',
    });
    setCreateOpen(true);
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.date || !eventForm.flowType || !eventForm.amount) return;
    try {
      await api.createCalendarEvent({
        title: eventForm.title,
        date: new Date(eventForm.date).toISOString(),
        color: eventForm.color,
        amount: parseFloat(eventForm.amount),
        currency: 'ARS',
        description: eventForm.description || undefined,
        category: 'custom',
        status: 'Pending',
        flowType: eventForm.flowType,
      });
      setCreateOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Error al crear evento');
    }
  };

  const handleDeleteEvent = async (id: number) => {
    try {
      await api.deleteCalendarEvent(id);
      setSelectedEvent(null);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar evento');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Calendario
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#fef2f2', borderLeft: '4px solid #ef4444' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Pasivos (Por Pagar)</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700} color="error.main">
                    {liabilities ? formatCurrency(liabilities.currentMonthTotal) : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Este mes</Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    {liabilities ? formatCurrency(liabilities.nextMonthTotal) : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Próximo mes</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#f0fdf4', borderLeft: '4px solid #22c55e' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Activos (Por Cobrar)</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {receivables ? formatCurrency(receivables.currentMonth.total) : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Este mes</Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    {receivables ? formatCurrency(receivables.nextMonth.total) : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Próximo mes</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Category Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Filtrar por categoría:</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <Chip
              key={key}
              label={config.label}
              icon={config.icon as React.ReactElement}
              onClick={() => handleCategoryToggle(key)}
              variant={selectedCategories.includes(key) ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedCategories.includes(key) ? config.color : 'transparent',
                color: selectedCategories.includes(key) ? 'white' : config.color,
                borderColor: config.color,
                '&:hover': {
                  bgcolor: selectedCategories.includes(key) ? config.color : `${config.color}20`,
                },
              }}
            />
          ))}
        </Stack>
      </Paper>

      {/* Calendar Header */}
      <Paper sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handlePrevMonth}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={handleNextMonth}>
              <ChevronRight />
            </IconButton>
            <Button startIcon={<Today />} onClick={handleToday} size="small">
              Hoy
            </Button>
          </Stack>
          <Typography variant="h6" fontWeight={600}>
            {MONTHS[month]} {year}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcCall />}
              onClick={() => setScheduleCallOpen(true)}
              size="small"
            >
              Agendar llamado
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreate}
              size="small"
            >
              Nuevo evento
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Calendar Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          {/* Day Headers */}
          <Grid container>
            {DAYS.map(day => (
              <Grid item xs key={day} sx={{ textAlign: 'center', py: 1, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={600}>{day}</Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Days */}
          <Grid container sx={{ minHeight: 500 }}>
            {calendarDays.map((date, index) => {
              const dayEvents = date ? eventsByDay[date.toDateString()] || [] : [];
              const isCurrentDay = date && isToday(date);

              return (
                <Grid
                  item
                  xs
                  key={index}
                  sx={{
                    minHeight: 100,
                    borderBottom: 1,
                    borderRight: (index + 1) % 7 !== 0 ? 1 : 0,
                    borderColor: 'divider',
                    bgcolor: isCurrentDay ? 'primary.50' : 'transparent',
                    p: 0.5,
                  }}
                >
                  {date && (
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={isCurrentDay ? 700 : 400}
                        sx={{
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          bgcolor: isCurrentDay ? 'primary.main' : 'transparent',
                          color: isCurrentDay ? 'white' : 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        {date.getDate()}
                      </Typography>
                      <Stack spacing={0.25}>
                        {dayEvents.slice(0, 3).map((event, i) => (
                          <Tooltip key={i} title={`${event.title}${event.amount ? ` - ${formatCurrency(event.amount, event.currency)}` : ''}`}>
                            <Chip
                              size="small"
                              label={event.title}
                              onClick={() => setSelectedEvent(event)}
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: event.color,
                                color: 'white',
                                cursor: 'pointer',
                                '& .MuiChip-label': {
                                  px: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 80,
                                },
                              }}
                            />
                          </Tooltip>
                        ))}
                        {dayEvents.length > 3 && (
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                            +{dayEvents.length - 3} más
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle sx={{ bgcolor: selectedEvent.color, color: 'white' }}>
              {selectedEvent.title}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Fecha</Typography>
                  <Typography>{new Date(selectedEvent.date).toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
                </Box>
                {selectedEvent.amount && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Monto</Typography>
                    <Typography variant="h6" fontWeight={700}>{formatCurrency(selectedEvent.amount, selectedEvent.currency)}</Typography>
                  </Box>
                )}
                {selectedEvent.flowType && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Tipo de compromiso</Typography>
                    <Chip
                      size="small"
                      label={selectedEvent.flowType === 'income' ? 'Ingreso / Por Cobrar' : 'Egreso / Por Pagar'}
                      color={selectedEvent.flowType === 'income' ? 'success' : 'error'}
                    />
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Categoría</Typography>
                  <Typography>{CATEGORY_CONFIG[selectedEvent.category]?.label || selectedEvent.category}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Estado</Typography>
                  <Chip
                    size="small"
                    label={selectedEvent.status}
                    color={selectedEvent.status === 'Overdue' ? 'error' : selectedEvent.status === 'Pending' ? 'warning' : 'success'}
                  />
                </Box>
                {selectedEvent.relatedEntity && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Relacionado con</Typography>
                    <Typography>{selectedEvent.relatedEntity}</Typography>
                  </Box>
                )}
                {selectedEvent.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Descripción</Typography>
                    <Typography>{selectedEvent.description}</Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            {selectedEvent.type === 'Call' && selectedEvent.status !== 'Completed' && (
              <Box sx={{ px: 3, pb: 1 }}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {whatsappUrl(selectedEvent.whatsApp || selectedEvent.phone) && (
                    <Button variant="contained" color="success" startIcon={<WhatsApp />} href={whatsappUrl(selectedEvent.whatsApp || selectedEvent.phone)!} target="_blank" rel="noopener">
                      WhatsApp
                    </Button>
                  )}
                  {telUrl(selectedEvent.phone || selectedEvent.whatsApp) && (
                    <Button variant="outlined" startIcon={<PhoneInTalk />} href={telUrl(selectedEvent.phone || selectedEvent.whatsApp)!}>
                      Llamar
                    </Button>
                  )}
                  <Button variant="contained" startIcon={<CheckCircle />} onClick={() => setCompleteCall(selectedEvent)}>
                    Hecho
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>Reprogramar</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {CALL_SHORTCUTS.map(sc => (
                    <Chip key={sc.days} label={sc.label} variant="outlined" onClick={async () => {
                      try {
                        await api.rescheduleCall(selectedEvent.id, { inDays: sc.days });
                        setSelectedEvent(null);
                        loadData();
                      } catch (err: any) {
                        setError(err?.response?.data?.message || 'No se pudo reprogramar');
                      }
                    }} />
                  ))}
                </Stack>
              </Box>
            )}
            <DialogActions>
              {(selectedEvent.category === 'custom' || selectedEvent.type === 'Call') && (
                <Button
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => handleDeleteEvent(selectedEvent.id)}
                >
                  Eliminar
                </Button>
              )}
              <Button onClick={() => setSelectedEvent(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ScheduleCallDialog open={scheduleCallOpen} onClose={() => setScheduleCallOpen(false)} onScheduled={loadData} />
      <CompleteCallDialog
        open={!!completeCall}
        callId={completeCall?.id ?? null}
        customerName={completeCall?.relatedEntity}
        onClose={() => setCompleteCall(null)}
        onDone={() => { setSelectedEvent(null); loadData(); }}
      />

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Evento</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Título"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              fullWidth
              required
            />

            {/* Tipo de flujo: Ingreso o Egreso */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Tipo de compromiso *
              </Typography>
              <ToggleButtonGroup
                value={eventForm.flowType}
                exclusive
                onChange={(_, value) => {
                  if (value) {
                    setEventForm({
                      ...eventForm,
                      flowType: value,
                      color: value === 'income' ? '#22c55e' : '#ef4444'
                    });
                  }
                }}
                fullWidth
              >
                <ToggleButton
                  value="income"
                  sx={{
                    flex: 1,
                    '&.Mui-selected': { bgcolor: '#dcfce7', color: '#166534', '&:hover': { bgcolor: '#bbf7d0' } }
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TrendingUp fontSize="small" />
                    <Box textAlign="left">
                      <Typography variant="body2" fontWeight={600}>Ingreso / Por Cobrar</Typography>
                      <Typography variant="caption" color="text.secondary">Activo - Dinero a recibir</Typography>
                    </Box>
                  </Stack>
                </ToggleButton>
                <ToggleButton
                  value="expense"
                  sx={{
                    flex: 1,
                    '&.Mui-selected': { bgcolor: '#fee2e2', color: '#991b1b', '&:hover': { bgcolor: '#fecaca' } }
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Payment fontSize="small" />
                    <Box textAlign="left">
                      <Typography variant="body2" fontWeight={600}>Egreso / Por Pagar</Typography>
                      <Typography variant="caption" color="text.secondary">Pasivo - Dinero a pagar</Typography>
                    </Box>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              label="Fecha"
              type="date"
              value={eventForm.date}
              onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Monto"
              type="number"
              value={eventForm.amount}
              onChange={(e) => setEventForm({ ...eventForm, amount: e.target.value })}
              fullWidth
              required
              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            />
            <TextField
              label="Descripción (opcional)"
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            disabled={!eventForm.title || !eventForm.date || !eventForm.flowType || !eventForm.amount}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarPage;
